import * as React from 'react'
import { Main as MainLayout } from 'layouts'
import { ShopWrapper } from 'sections/PodAtc/PodAtc'
import { AppMetadata } from 'components/AppMetadata'
import {
	BlanketProducts,
	BundleProducts,
	ComforterProducts,
	FoundationProducts,
	HypericeProducts,
	MattressProducts,
	NormaTecProducts,
	PillowProducts,
	ProtectorProducts,
	SheetProducts,
	SprayProducts,
} from 'products/allProducts'
import rootStore, { RootStore } from 'stores/index'
import { Product, ProductCore, ProductShopDataType } from 'products/types'
import { VanityCoverShopHeader } from 'components/VanityCoverShopHeader'
import { inject, observer } from 'mobx-react'
import { isPodBedProductId, isPodCoverHandle, isPodCoverProductId, mapUrlToShop } from 'products/utils'
import { LiveStickyBar } from 'components/StickyBar'
import { Waypoint } from 'react-waypoint'
import { SKIP_MEMBERSHIP_CHECKOUT_KEY } from 'constants/index'
// import { Shop } from 'components/WebEv/Shop'
import Script from 'next/script'
import { PriceManager } from 'prices'
import { trackCoverShopPageVisit } from 'events/index'
import Head from 'next/head'
// import { PDPMain } from 'components/WebEv/Shop/Sections'
import { PodUpsell } from 'components/PodUpsell'
import { ConnectedShop } from 'components/Phantom/_pageCompositions/Shop'
import { Pod4ShopComparison } from 'components/Pod4ShopComparison'
import { Pod4ShopNews } from 'components/Pod4ShopNews'
import { Pod4ShopQualityPromises } from 'components/Pod4ShopQualityPromises'
import { Pod4FAQ } from 'components/Pod4FAQ'
import { getFAQs } from 'components/_sections/FAQ/_faqs'

export interface VariantType {
	id: number
	name: string
	price: number
}

export interface Props {
	handle?: string
	path: string
	product?: ProductCore
	showPrice?: boolean
	showStock?: boolean
	pathName?: string
	rootStore?: RootStore
}

interface State {
	ContentSection?: any
	sentTracking: boolean
	selectedProduct?: Product
	showStickyBar?: boolean
}

export function parseShopUrl(path: string) {
	let url = path
	if (url.includes('?')) {
		url = url.split('?')[0]
	}
	if (url.includes('#')) {
		url = url.split('#')[0]
	}

	if (url.charAt(url.length - 1) === '/') {
		url = url.substring(0, url.length - 1)
	}
	return url
}

async function getContent(id: string | undefined) {
	if (!typeof window || !id) {
		return null
	}
	let ContentComponent = undefined
	switch (id) {
		case ProtectorProducts.PodProtector.id:
			ContentComponent = await import('components/AccessoryCTA/AccessoryCTA')
			break
		case FoundationProducts.SimpleFoundation.id:
			ContentComponent = await import('components/AccessoryCTA/AccessoryCTA')
			break
		case BlanketProducts.BrandedGravity.id:
			ContentComponent = await import('components/AccessoryCTA/AccessoryCTA')
			break
		case SheetProducts.SheetSet.id:
			ContentComponent = await import('components/AccessoryCTA/AccessoryCTA')
			break
		case BundleProducts.MelatoninProBundle.id:
			ContentComponent = await import('components/AccessoryCTA/AccessoryCTA')
			break
		case PillowProducts.CarbonAirPillow.id:
		case PillowProducts.CarbonAirPillowKing.id:
			ContentComponent = await import('components/AccessoryCTA/AccessoryCTA')
			break
		case BundleProducts.SleepEssentialBundle.id:
			ContentComponent = await import('components/AccessoryCTA/AccessoryCTA')
			break
		case ComforterProducts.AirLiteComforter.id:
			ContentComponent = await import('components/AccessoryCTA/AccessoryCTA')
			break
		case SprayProducts.LavenderSpray.id:
			ContentComponent = await import('components/AccessoryCTA/AccessoryCTA')
			break
		case HypericeProducts.HypervoltPlus.id:
			ContentComponent = await import('components/AccessoryCTA/AccessoryCTA')
			break
		case HypericeProducts.VenomBack.id:
			ContentComponent = await import('components/AccessoryCTA/AccessoryCTA')
			break
		case NormaTecProducts.LegPulse2.id:
			ContentComponent = await import('components/AccessoryCTA/AccessoryCTA')
			break
		case MattressProducts.FiveLayerDiscounted.id:
			ContentComponent = await import('sections/_products/MattressOnly')
			break
	}

	if (isPodBedProductId(id)) {
		ContentComponent = await import('sections/_products/Pod')
	}
	if (isPodCoverProductId(id)) {
		ContentComponent = await import('sections/_products/PodTechCover')
	}

	if (ContentComponent) {
		return ContentComponent.default
	}
	return null
}

export function getProductProps(x: string): Props {
	const path = parseShopUrl(x)
	const productData: ProductShopDataType = rootStore.productStore.getShopPageProducts(mapUrlToShop(path))
	const { products, defaultProductSelected } = productData
	const product = products[defaultProductSelected]
	const handle = product.handle
	return { handle, product, path }
}

class ProductCorePageComponent extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props)
		this.state = {
			ContentSection: null,
			sentTracking: false,
			showStickyBar: true,
		}
	}

	async componentDidMount() {
		window.dataLayer?.push({ product_id: this.props.product?.id.toString() })
		const section = await getContent(this.props.product?.id)
		this.setState({ ContentSection: section })
		if (localStorage.getItem('member') === 'true') {
			sessionStorage.setItem(SKIP_MEMBERSHIP_CHECKOUT_KEY, 'true')
		} else {
			sessionStorage.removeItem(SKIP_MEMBERSHIP_CHECKOUT_KEY)
		}

		setTimeout(() => {
			const settingsStore = this.props.rootStore?.settingsStore
			const priceStore = this.props.rootStore?.priceStore
			const email = localStorage.getItem('email')
			if (window.location.href.includes('/product/pod-cover/') && email) {
				trackCoverShopPageVisit({
					email,
					code: settingsStore.globalPromoCode,
					amount: priceStore.totalProductDiscountNumber,
					region: settingsStore.currentRegion,
				})
			}
		}, 2000)
	}

	async componentDidUpdate(prevProps: Props) {
		if (prevProps.product?.id !== this.props.product?.id) {
			window.dataLayer?.push({ product_id: this.props.product?.id.toString() })
			const section = await getContent(this.props.product?.id)
			this.setState({ ContentSection: section })
		}
	}

	render() {
		const { handle, path } = this.props

		const { ContentSection } = this.state
		const { settingsStore, productStore, priceStore } = this.props.rootStore!

		const pagePath = path || (process.browser ? window.location.pathname : '')
		const shop = mapUrlToShop(pagePath)
		const isCoverShopPage = process.browser && isPodCoverHandle(handle || '')
		const vanityPagePath = settingsStore && settingsStore.settings ? settingsStore.settings.page : null
		const onVanity = (isCoverShopPage && vanityPagePath === '/product/pod-cover') || vanityPagePath === 'tim'

		const shopData = productStore.getShopPageProducts(shop)
		const memberShopPage = pagePath.includes('eba234937b8d54e2d519b318707ea9713e6087b402b40670319c4c1e166e9208')
		const region = settingsStore.currentRegion

		const cartLoading = this.props.rootStore?.cartStoreNew.updatingCart // DO NOT delete, this forces a rerender in Shopmain for some reason

		const StructuredDataSnippet = () => {
			if (handle !== 'pod-3-cover-mattress' && !isCoverShopPage) return null

			const mainProductIndex = shopData.defaultProductSelected
			const productSKU = isCoverShopPage ? '10512-001' : '21500-000'
			const defaultProduct = shopData.products[mainProductIndex]
			const defaultVariant = defaultProduct.variants[PriceManager.getRegionDefaultVariant(region)]?.id
			const priceMap = priceStore.getVariantPrices(defaultVariant || defaultProduct.variants.queen.id)
			const productPrice = parseFloat(priceMap[priceStore.currency].price!)
			const regionPrefix = region && region !== 'us' ? region + '/' : ''
			const productURL = isCoverShopPage ? `https://www.eightsleep.com/${regionPrefix}product/pod-cover/` : `https://www.eightsleep.com/${regionPrefix}product/pod-cover-mattress/`

			return (
				<Script
					type="application/ld+json"
					id={`structured-data-script-${handle}`}
					dangerouslySetInnerHTML={{
						__html: `
							{
								"@context": "https://schema.org/",
								"@type": "Product",
								"name": "${shopData.products[mainProductIndex].name}",
								"image": [
									"${shopData.images[mainProductIndex][0].src}",
									"${shopData.images[mainProductIndex][1].src}",
									"${shopData.images[mainProductIndex][2].src}"
								],
								"description": "${shopData.products[mainProductIndex].description}",
								"sku": "${productSKU}",
								"brand": {
									"@type": "Brand",
									"name": "Eight Sleep"
								},
								"offers": {
									"@type": "Offer",
									"url": "${productURL}",
									"price": ${productPrice},
									"priceCurrency": "${priceStore.currency}",
									"itemCondition": "https://schema.org/NewCondition",
									"availability": "https://schema.org/InStock"
								}
							}
						`,
					}}
				/>
			)
		}

		if (isPodCoverHandle(handle || '')) {
			return (
				<MainLayout>
					<Head>
						<link
							rel="prefetch"
							href="https://eight-eightsleep.s3.amazonaws.com/cover_falling_bedroom.mp4"
						/>
						<link
							rel="prefetch"
							href="https://eight-eightsleep.s3.amazonaws.com/cover_falling_white.mp4"
						/>
						<link
							rel="preload"
							as="image"
							href="https://eightsleep.imgix.net/shop_pod3_mattress_size_full.png"
						/>
						<link
							rel="preload"
							as="image"
							href="https://eightsleep.imgix.net/shop_pod3_mattress_size_queen.png"
						/>
						<link
							rel="preload"
							as="image"
							href="https://eightsleep.imgix.net/pod3_size_king.jpg"
						/>
						<link
							rel="preload"
							as="image"
							href="https://eightsleep.imgix.net/pod3_size_caliking.jpg"
						/>
						<link
							rel="preload"
							as="image"
							href="https://eightsleep.imgix.net/shop_pod4_mattress_size_full.png"
						/>
						<link
							rel="preload"
							as="image"
							href="https://eightsleep.imgix.net/shop_pod4_mattress_size_queen.png"
						/>
						<link
							rel="preload"
							as="image"
							href="https://eightsleep.imgix.net/pod4_size_king.jpg"
						/>
						<link
							rel="preload"
							as="image"
							href="https://eightsleep.imgix.net/pod4_size_caliking.jpg"
						/>
					</Head>
					<StructuredDataSnippet />
					<AppMetadata
						path={pagePath}
						key={pagePath}
					/>
					<ConnectedShop />
					<Pod4ShopNews />
					<Pod4ShopQualityPromises />
					<Pod4ShopComparison ctaHref="#shop" />
					<Pod4FAQ faq={getFAQs('cover', rootStore.settingsStore.currentRegion, rootStore.settingsStore.settings.promoCode === 'members')} />
				</MainLayout>
			)
		}

		if (memberShopPage) {
			return (
				<MainLayout>
					<StructuredDataSnippet />
					<AppMetadata
						path={pagePath}
						key={pagePath}
					/>

					<ConnectedShop />

					{ContentSection && (
						<ContentSection
							handle={handle}
							product={this.state.selectedProduct}
							rootStore={this.props.rootStore}
						/>
					)}
				</MainLayout>
			)
		}

		return (
			<MainLayout>
				<StructuredDataSnippet />
				<AppMetadata
					path={pagePath}
					key={pagePath}
				/>
				{memberShopPage ? (
					<>
						<LiveStickyBar
							productTitle="Pod 3 Member Upgrade"
							shopUrl="#main_shop"
							podOrCover="upgrade"
							hide={!this.state.showStickyBar}
							anchorLink
							noExpire
						/>
						{/* <section>
							<HomepageHeroPod3Shop discount={PriceManager.formatPriceToCurrencyNoDecimal(500, priceStore.currency)} />
						</section>
						<div
							id={'hero-anchor'}
							style={{ position: 'relative', top: '-100px' }}
						/>
						<div className="bg-white inner-paddings-minor-top"></div> */}
					</>
				) : null}
				{onVanity ? <VanityCoverShopHeader settings={settingsStore.settings} /> : null}
				<Waypoint
					onEnter={() => this.setState({ showStickyBar: false })}
					onLeave={() => this.setState({ showStickyBar: true })}
				>
					<div>
						<ShopWrapper
							{...shopData}
							path={pagePath}
							rootStore={this.props.rootStore!}
							cartStoreNew={this.props.rootStore?.cartStoreNew}
							settingsStore={this.props.rootStore?.settingsStore}
							priceStore={this.props.rootStore?.priceStore}
							key={pagePath}
						/>
					</div>
				</Waypoint>

				{ContentSection && (
					<ContentSection
						handle={handle}
						product={this.state.selectedProduct}
						rootStore={this.props.rootStore}
					/>
				)}

				<PodUpsell />
			</MainLayout>
		)
	}
}

export const ProductCorePage = inject('rootStore')(observer(ProductCorePageComponent))
